/**
 * Stripe Payment Integration Library
 *
 * Provides frontend functions to interact with Stripe via Supabase Edge Functions.
 * Handles billing portal access for subscription management.
 *
 * NOTE: Volume-based pricing is now handled via create-checkout-session Edge Function
 * using VOLUME_PRICING_TIERS from src/config/volumePricing.ts
 *
 * @module stripe
 */

import { supabase } from '@/integrations/supabase/client';
import { api, APIError } from '@/services/apiClient';
import { logger } from '@/utils/logger';

/**
 * Error thrown when Stripe operations fail
 */
export class StripeError extends Error {
  constructor(message: string, public originalError?: unknown) {
    super(message);
    this.name = 'StripeError';
  }
}

/**
 * @deprecated This function uses the old subscription-based pricing model.
 * New checkouts should use the create-checkout-session Edge Function directly,
 * which handles volume-based pricing from src/config/volumePricing.ts
 *
 * Kept for backwards compatibility with legacy subscribers only.
 */
export async function redirectToCheckout(
  _tier: string,
  _interval: 'month' | 'year' | 'one_time' = 'month'
): Promise<void> {
  throw new StripeError(
    'redirectToCheckout is deprecated. Use the onboarding flow which handles volume-based pricing automatically.'
  );
}

/**
 * Redirect user to Stripe Customer Portal for subscription management
 *
 * The portal allows users to:
 * - Cancel their subscription
 * - Update payment methods
 * - View billing history
 * - Download invoices
 *
 * @returns Promise that resolves when redirect is initiated
 * @throws {StripeError} If portal creation fails
 *
 * @example
 * // Add to user settings page
 * <Button onClick={() => redirectToBillingPortal()}>
 *   Manage Billing
 * </Button>
 */
export async function redirectToBillingPortal(): Promise<void> {
  try {
    logger.debug('🔄 Creating Stripe billing portal session...');

    // Refresh the session to ensure we have a valid JWT token
    const { data: sessionData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      logger.error('❌ Session refresh failed:', refreshError);
      throw new StripeError('Votre session a expiré. Veuillez vous reconnecter.');
    }

    if (!sessionData.session) {
      throw new StripeError('Vous devez être connecté pour accéder au portail de facturation.');
    }

    logger.debug('✅ Session refreshed successfully');

    // Call customer-portal edge function (edge function will get customer from JWT)
    const data = await api.createBillingPortalSession();

    logger.debug('📝 Billing portal response:', data);

    if (!data?.url) {
      logger.error('❌ No portal URL in response:', data);
      throw new StripeError('Aucune URL de portail retournée par le serveur. Veuillez réessayer.');
    }

    logger.debug('✅ Billing portal session created, redirecting to:', data.url);

    // Redirect to Stripe portal
    window.location.href = data.url;
  } catch (error: any) {
    logger.error('❌ Billing portal error:', error);
    if (error instanceof StripeError) {
      throw error;
    }
    // Extract error message from API error
    // APIError stores user-friendly message in 'code' field (from data.error)
    // and support message in 'message' field (from data.message)
    const message = error?.code || error?.message || 'Erreur inattendue lors de l\'accès au portail de facturation';
    throw new StripeError(message, error);
  }
}

/**
 * Check if user has an active subscription
 *
 * @returns Promise with subscription status
 *
 * @example
 * const { subscribed, product_id } = await checkSubscription();
 * if (subscribed) {
 *   logger.debug('Active subscription:', { product_id });
 * }
 */
export async function checkSubscription(): Promise<{
  subscribed: boolean;
  product_id?: string;
  subscription_end?: string;
}> {
  try {
    const data = await api.checkSubscription();

    return {
      subscribed: data?.subscribed ?? false,
      product_id: data?.product_id ?? undefined,
      subscription_end: data?.subscription_end ?? undefined
    };
  } catch (error) {
    if (error instanceof APIError) {
      logger.error('❌ Subscription check failed:', {
        code: error.code,
        message: error.message,
        status: error.status
      });
    } else {
      logger.error('❌ Unexpected error checking subscription:', error);
    }
    return { subscribed: false };
  }
}

/**
 * Get user's payment status from database
 * Uses the user_payment_status view for consolidated access info
 *
 * @returns Promise with user's payment/access status
 *
 * @example
 * const status = await getUserPaymentStatus();
 * if (status.has_active_access) {
 *   // User has either active subscription or valid credits
 * }
 */
export async function getUserPaymentStatus(): Promise<{
  has_active_access: boolean;
  subscription_status?: string;
  subscription_expires?: string;
  credits_remaining?: number;
  credits_expire_at?: string;
} | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    const { data, error } = await supabase
      .from('user_payment_status')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) {
      logger.error('❌ Error fetching payment status:', error);
      return null;
    }

    return data;
  } catch (error) {
    logger.error('❌ Unexpected error fetching payment status:', error);
    return null;
  }
}
